export const VCA_ONBOARDING_CONTENT = {
  slug: 'setup-vca',
  logo: 'VideaAI',
  title: 'VideaAI Setup',
  description:
    'Step into the future of dentistry with VideaAI by VideaHealth! Your AI journey starts now by completing a quick three-minute setup process, ensuring a seamless blending of the VideaAI medical device into your daily routine. \nGet ready for success with helpful tips by clicking “Get Started.”',
  closeUrl: 'huddle',
  slides: [
    {
      id: 0,
      title: 'Welcome to VideaAI',
    },
    {
      id: 100,
      title: 'What to expect from Dental Image AI',
      logo: 'VideaAI',
      template: 'intro',
      isEmbed: false,
      sections: [
        {
          title: 'AI findings from radiographs',
          color: 'success',
          images: [],
          paragraphs: [
            {
              text: 'VideaAI, equipped with the collective expertise of hundreds of experienced dentists, is a cleared medical device, giving you peace of mind regarding its safety for patient use. \n\nAI findings are automatically marked on your x-rays, serving as a helpful guide in your diagnostic process. Think of VideaAI as your trusty assistant, directing your attention to specific areas for evaluation and granting you (providers) complete diagnostic autonomy in making the final treatment decision.',
              color: 'default',
              class: 'content-large',
            },
          ],
        },
        {
          title: 'The Huddle',
          color: 'success',
          paragraphs: [],
          images: [
            {
              alt: 'Diagram of huddle patient information',
              imageUri: '/assets/images/onboarding/huddle-illust-01.png',
              scale: '3x',
              format: 'block',
              caption:
                'When you take radiographs, they are added to the “Huddle” view in real time. \n\nEach patient has details on treatments, tasks, and a summary of AI findings.',
            },
          ],
        },
      ],
    },
    {
      id: 200,
      title: 'The VideaAI image viewer and controls',
      logo: 'VideaAI',
      template: 'intro',
      isImage: false,
      isEmbed: false,
      sections: [
        {
          title: 'Viewer Interface',
          color: 'success',
          images: [
            {
              alt: 'Diagram of viewer interface',
              imageUri: '/assets/images/onboarding/viewer-01.png',
              scale: '2.5x',
              format: 'inline-block',
              caption:
                'Switch between boxes and shape for annotations with the Patient View toggle (if available).',
            },
            {
              alt: 'Diagram of toggle to hide or show annotations',
              imageUri: '/assets/images/onboarding/viewer-02.png',
              scale: '2x',
              format: 'inline-block',
              caption:
                'To show or hide annotations, use the toggles (for restorative, perio, or endo findings).',
            },
            {
              alt: 'Diagram of toggle to hide or show annotations',
              imageUri: '/assets/images/onboarding/viewer-03.png',
              scale: '2.5x',
              format: 'inline-block',
              captionTitle: 'Keyboard Shortcuts',
              caption:
                'Most actions can be done with the keyboard. A keyboard icon displays all shortcuts.',
            },
          ],
          paragraphs: [],
        },
        {
          order: 2,
          title: 'Viewing historical radiographs',
          color: 'success',
          paragraphs: [],
          images: [
            {
              alt: 'Diagram of image comparison',
              imageUri: '/assets/images/onboarding/viewer-04.png',
              scale: '2x',
              format: 'inline-block',
              caption:
                'To compare radiographs across visits, select the “compare visits” button (when available).',
            },
            {
              alt: 'Diagram of image controls',
              imageUri: '/assets/images/onboarding/viewer-05.png',
              scale: '2x',
              format: 'inline-block',
              caption:
                'To adjust the brightness and contrast for radiographs, use the image tools.',
            },
          ],
        },
      ],
    },
    {
      id: 300,
      title: 'What to expect from AI Caries Detection',
      subtitle: 'Potential carious lesions: annotations',
      color: 'success',
      logo: 'VideaAI',
      template: 'default',
      isImage: false,
      isEmbed: false,
      sections: [
        {
          title: 'Display annotation with bounding-box',
          color: 'success',
          icon: 'check',
          images: [
            {
              alt: 'Diagram of tooth with caries annotation',
              imageUri: '/assets/images/onboarding/caries-01.png',
              scale: '2.5x',
              format: 'block',
              caption:
                'Identifies potential carious lesions using boxes, used by providers to diagnose disease.',
            },
          ],
          paragraphs: [],
        },
        {
          title: 'Display annotation with isocontour',
          color: 'success',
          icon: 'check',
          images: [
            {
              alt: 'Diagram of tooth with caries isocontour',
              imageUri: '/assets/images/onboarding/caries-02.png',
              scale: '2.5x',
              format: 'block',
              caption:
                'Draws out potential carious lesions, used by the clinical team to educate patients.',
            },
          ],
          paragraphs: [],
        },
        {
          color: 'success',
          images: [
            {
              alt: 'Diagram of tooth with caries',
              imageUri: '/assets/images/onboarding/caries-03.png',
              scale: '2.5x',
              format: 'block',
              captionTitle: null,
              caption: 'Inter-proximal, Occlusal, Gross / Root, Buccal/lingual',
            },
          ],
          paragraphs: [],
        },
        {
          images: [],
          paragraphs: [
            {
              text: 'Incipient \n Moderate \n Severe',
              color: 'default',
              class: 'content-medium',
            },
            {
              title: 'Potential False Positives',
              text: 'Crown margins \n Cervical burnout \n Images that are overexposed',
              icon: 'warning',
              color: 'warning',
              class: 'content-medium',
            },
            {
              title: 'Potential False Negatives',
              text: 'Overlapping teeth \n Some kinds of occlusal/lingual/buccal caries',
              icon: 'warning',
              color: 'warning',
              class: 'content-medium',
            },
          ],
        },
      ],
    },
    {
      id: 400,
      title: 'What to expect from AI RBL Detection',
      subtitle: 'Potential radiographic bone level: annotations',
      color: 'success',
      logo: 'VideaAI',
      template: 'default',
      isImage: false,
      isEmbed: false,
      sections: [
        {
          title:
            'On bitewing radiographs, bone level is measured in millimeters',
          color: 'success',
          icon: 'check',
          images: [
            {
              alt: 'Diagram of tooth with rbl annotation',
              imageUri: '/assets/images/onboarding/rbl-01.png',
              scale: '2.25x',
              format: 'block',
            },
          ],
          paragraphs: [],
        },
        {
          title:
            'On periapical radiographs, bone level is measured in % from the root',
          color: 'success',
          icon: 'check',
          images: [
            {
              alt: 'Diagram of teeth with rbl lines',
              imageUri: '/assets/images/onboarding/rbl-02.png',
              scale: '2.25x',
              format: 'block',
            },
          ],
          paragraphs: [],
        },
        {
          images: [],
          paragraphs: [
            {
              text: 'Radiographic bone level measurements are a requirement of American Academy of Periodontology staging and grading when diagnosing potential periodontal disease, combined with perio charting and other clinical factors.',
              color: 'default',
              class: 'content-bold',
            },
            {
              title:
                'RBL measurements will not necessarily correspond to periodontal chart depth measurements.',
              text: '',
              icon: 'warning',
              color: 'warning',
              class: 'content-medium',
            },
          ],
        },
      ],
    },
    {
      id: 500,
      title: 'What to expect from AI RBL Detection',
      subtitle: 'Choose your RBL practice default colors',
      color: 'success',
      logo: 'VideaAI',
      template: 'default',
      isImage: false,
      isEmbed: true,
      sections: [
        {
          title: null,
          subtitle: null,
          color: 'default',
          icon: null,
          images: [],
          paragraphs: [
            {
              title: null,
              text: 'Videa Perio Assist is a medical device focused on measurement of radiographic bone level (RBL). To adjust the thresholds for the colors assigned to the measurements, adjust the slider.',
              icon: null,
              color: 'default',
              class: 'content-large',
            },
          ],
        },
        {
          title: null,
          subtitle: null,
          color: 'default',
          icon: null,
          embed: 'rblSettings',
          paragraphs: [],
        },
        {
          title: null,
          subtitle: null,
          color: 'default',
          icon: null,
          paragraphs: [
            {
              title: null,
              text: 'The American Academy of Periodontology associates the following RBL measurements with these Periodontitis stages:\n\nStage I: Coronal third (<15%)\nStage II: Coronal third (<30%)\n Stage III: Extending to middle third of root and beyond \n Stage IV: Extending to middle third of root and beyond',
              icon: null,
              color: 'default',
              class: 'content-medium',
            },
          ],
        },
      ],
    },
    {
      id: 500,
      title: 'Congratulations!',
    },
  ],
};
